import type { BomLatestRow, BomLine, BomVersion, BomVersionIn, Entity, Material, Product } from "./api";

export type DraftLine = { xero_item_code: string; qty: string; supplied_by: "ketosource" | "factory" };
export type ListRow = { sku: string; entity_id: Entity; product: Product; latest: BomLatestRow | null; isCase: boolean };
const QTY = /^\d{1,12}(\.\d{1,6})?$/;

/** One row per produced SKU of the entity (products picker, produced_only), joined to the latest BOM. */
export function listRows(entity: Entity, products: Product[], boms: BomLatestRow[]): ListRow[] {
  const latest = new Map(boms.filter((row) => row.entity_id === entity).map((row) => [row.sku, row]));
  return products.map((product) => ({ sku: product.xero_sku, entity_id: entity, product, latest: latest.get(product.xero_sku) ?? null, isCase: product.units_per_case !== null }))
    .sort((a, b) => a.sku.localeCompare(b.sku));
}
export function entityForLocation(location: string | null): Entity | null {
  if (location === "UK") return "LTD";
  if (location === "EU") return "OU";
  return null;
}
export function listRowsAll(products: Product[], bomsByEntity: Partial<Record<Entity, BomLatestRow[]>>): ListRow[] {
  const rows = products.flatMap((product) => {
    const entity = entityForLocation(product.location);
    return entity ? listRows(entity, [product], bomsByEntity[entity] ?? []) : [];
  });
  return [...new Map(rows.map((row) => [`${row.entity_id}:${row.sku}`, row])).values()]
    .sort((a, b) => a.entity_id.localeCompare(b.entity_id) || a.sku.localeCompare(b.sku));
}
export function missingFor(rows: ListRow[]): ListRow[] { return rows.filter((row) => row.latest === null && !row.isCase); }
export function missingWords(rows: ListRow[]): string {
  const missing = missingFor(rows);
  return (["LTD", "OU"] as const).flatMap((entity) => {
    const count = missing.filter((row) => row.entity_id === entity).length;
    return count ? [`${count} ${entity === "LTD" ? "UK" : "EU"} SKUs without a BOM`] : [];
  }).join(" and ");
}
export function lineProblems(lines: DraftLine[]): Array<string | null> {
  const seen = new Set<string>();
  return lines.map((line) => {
    if (!line.xero_item_code) return "Pick a material.";
    if (seen.has(line.xero_item_code)) return "This material is already on the BOM.";
    seen.add(line.xero_item_code);
    if (!QTY.test(line.qty) || Number(line.qty) <= 0) return "Quantity must be above zero (up to 6 decimals).";
    return null;
  });
}
export function versionPayload(sku: string, entity: Entity, lines: DraftLine[], note: string): BomVersionIn {
  return { sku, entity_id: entity, lines: lines.map((line) => ({ xero_item_code: line.xero_item_code, qty_per_retail_unit: line.qty, supplied_by: line.supplied_by })), ...(note.trim() ? { note: note.trim() } : {}) };
}
export function draftFrom(lines: BomLine[]): DraftLine[] { return lines.map((line) => ({ xero_item_code: line.xero_item_code, qty: line.qty_per_retail_unit, supplied_by: line.supplied_by })); }
export function diffWords(version: BomVersion): string {
  const parts: string[] = [];
  if (version.diff.added.length) parts.push(`added ${version.diff.added.join(", ")}`);
  if (version.diff.removed.length) parts.push(`removed ${version.diff.removed.join(", ")}`);
  if (version.diff.changed.length) parts.push(`changed ${version.diff.changed.join(", ")}`);
  return parts.length ? parts.join("; ") : version.version_no === 1 ? "first version" : "no line changes";
}
export function regeneratedWords(row: BomLatestRow, versions: BomVersion[] | undefined): string | null {
  if (!row.regenerated_from_version_id) return null;
  const source = versions?.find((version) => version.bom_version_id === row.regenerated_from_version_id);
  return source ? `Regenerated from retail v${source.version_no}` : "Regenerated from the retail BOM";
}
export function materialLabel(material: Material): string { return `${material.name} · ${material.xero_item_code} (${material.uom})`; }
export function otherEntity(entity: Entity): Entity | null { return entity === "LTD" ? "OU" : entity === "OU" ? "LTD" : null; }
